import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import PreviewIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { bindMenu, bindTrigger, usePopupState } from "material-ui-popup-state/hooks";

import VerticalDraggableList from "../../../views/favoriteReport/VerticalDraggableList";
import ColumnLayoutPreviewModal from "../../../views/favoriteReport/ColumnLayoutPreviewModal";

function EmptyStatePrimaryActionButton({ label, onClick, disabled = false, disabledTooltip = "" }) {
  const button = (
    <Button
      variant="outlined"
      size="medium"
      color="primary"
      onClick={onClick}
      disabled={disabled}
      sx={{ textTransform: "none" }}
    >
      {label}
    </Button>
  );
  if (disabled && disabledTooltip) {
    return (
      <Tooltip title={disabledTooltip}>
        <Box component="span" sx={{ display: "inline-block" }}>
          {button}
        </Box>
      </Tooltip>
    );
  }
  return button;
}

export default function OrderedFieldsSection({
  title,
  fields,
  onReorder,
  onRemove,
  onAdd,
  availableFields,
  maxFields = Infinity,
  mandatoryFields = [],
  addButtonLabel = "Add Another Field",
  emptyStateText = "No fields selected",
  /** When set, shown instead of `emptyStateText` when the ordered list is empty. */
  emptyStateContent = null,
  /**
   * When the list is empty, show this button instead of the add-field menu (e.g. store card "set custom" seed).
   * `{ label, onClick, disabled?, disabledTooltip? }`
   */
  emptyStatePrimaryAction = null,
  enablePreview = false,
  children,
}) {
  const popupState = usePopupState({ variant: "popover", popupId: `${title}-ordered-fields` });
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const draggableFields = useMemo(
    () =>
      (fields || []).map((field, index) => ({
        id: field[0],
        content: field[2],
        fieldData: field,
        excluded: index >= maxFields,
        isMandatory: mandatoryFields.includes(field[0]),
      })),
    [fields, maxFields, mandatoryFields]
  );

  const fieldsToAdd = useMemo(
    () =>
      (availableFields || [])
        .filter(availableField => !(fields || []).some(field => field[0] === availableField[0]))
        .sort((a, b) => (a[2] || "").localeCompare(b[2] || "")),
    [availableFields, fields]
  );

  const handleAddField = field => {
    onAdd(field);
    popupState.close();
  };

  return (
    <Paper
      sx={{
        p: 0,
        display: "flex",
        flexDirection: "column",
        maxHeight: 600,
        flexShrink: 0,
        overflow: "hidden",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        sx={{
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography>{title}</Typography>
        {enablePreview && (
          <Button
            variant="outlined"
            size="small"
            color="primary"
            startIcon={<PreviewIcon />}
            onClick={() => setIsPreviewModalOpen(true)}
            sx={{ textTransform: "none", fontWeight: 500, boxShadow: "none" }}
          >
            Preview Columns
          </Button>
        )}
      </Box>

      {children}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 1 }}>
        {(fields || []).length > 0 ? (
          <>
            <VerticalDraggableList
              tags={draggableFields}
              onChange={reorderedTags => onReorder(reorderedTags.map(tag => tag.fieldData))}
              onRemove={onRemove}
              maxFields={maxFields}
            />
            {fieldsToAdd.length > 0 && (
              <Box sx={{ mt: 1, ml: 1, display: "flex", justifyContent: "flex-start" }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  {...bindTrigger(popupState)}
                  size="medium"
                  color="primary"
                  sx={{ textTransform: "none" }}
                >
                  {addButtonLabel}
                </Button>
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ py: 2, textAlign: "center" }}>
            {emptyStateContent != null ? (
              <Box sx={{ mb: 2, textAlign: "left", px: 2 }}>{emptyStateContent}</Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {emptyStateText}
              </Typography>
            )}
            {emptyStatePrimaryAction ? (
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <EmptyStatePrimaryActionButton {...emptyStatePrimaryAction} />
                {emptyStatePrimaryAction.disabled && fieldsToAdd.length > 0 && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    {...bindTrigger(popupState)}
                    size="medium"
                    color="primary"
                    sx={{ textTransform: "none" }}
                  >
                    {addButtonLabel}
                  </Button>
                )}
              </Box>
            ) : (
              fieldsToAdd.length > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  {...bindTrigger(popupState)}
                  size="medium"
                  color="primary"
                  sx={{ textTransform: "none" }}
                >
                  {addButtonLabel}
                </Button>
              )
            )}
          </Box>
        )}
      </Box>

      <Menu
        {...bindMenu(popupState)}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: "250px",
          },
        }}
      >
        {fieldsToAdd.map(field => (
          <MenuItem key={field[0]} onClick={() => handleAddField(field)}>
            {field[2]}
          </MenuItem>
        ))}
      </Menu>

      {enablePreview && (
        <ColumnLayoutPreviewModal
          open={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          columnTags={(fields || []).slice(0, maxFields).map(field => ({
            id: field[0],
            content: field[2] || field[1],
          }))}
        />
      )}
    </Paper>
  );
}
